import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useApi } from "@/hooks/use-api";
import { EmptyState, ErrorState } from "@/components/common/EmptyState";
import { EventsTable } from "@/components/events/EventsTable";
import { EventDetailSheet } from "@/components/events/EventDetailSheet";
import { SEVERITY } from "@/lib/status";
import { cn } from "@/lib/utils";

/* Hendelser: the register of what has happened. The counts are one line under the title – not a row of boxes –
   and the filters sit on one line above the table. No frame around the table. */
export default function EventsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [severity, setSeverity] = useState([]);
  const [status, setStatus] = useState("active");
  const [kind, setKind] = useState("all");
  const [plantId, setPlantId] = useState("all");
  const [days, setDays] = useState("90");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const params = useMemo(
    () => ({
      severity: severity.length ? severity.join(",") : undefined,
      status: status === "active" ? "open,acknowledged" : status === "all" ? undefined : status,
      kind: kind === "all" ? undefined : kind,
      plant_id: plantId === "all" ? undefined : plantId,
      days: Number(days),
      q: q.trim() || undefined,
      limit: 300,
    }),
    [severity, status, kind, plantId, days, q],
  );
  const { data, isLoading, isError, refetch, isFetching } = useApi("/events", params, { keepPrevious: true, staleTime: 30_000 });
  const plants = useApi("/plants");
  const counts = data?.counts;

  /* «Se hendelsen» links arrive as /hendelser?id=… – open that event directly */
  const wantedId = searchParams.get("id");
  const wanted = useApi(`/events/${wantedId}`, {}, { enabled: !!wantedId });
  useEffect(() => {
    if (wantedId && wanted.data) {
      setSelected(wanted.data);
      setSearchParams({}, { replace: true });
    }
  }, [wantedId, wanted.data, setSearchParams]);

  const reset = () => {
    setSeverity([]);
    setStatus("all");
    setKind("all");
    setPlantId("all");
    setDays("365");
    setQ("");
  };
  const filtered = severity.length || status !== "active" || kind !== "all" || plantId !== "all" || days !== "90" || q;

  if (isError) return <ErrorState message="Kunne ikke hente hendelser." onRetry={refetch} />;

  return (
    <div className="space-y-6" data-testid="events-page">
      <header data-testid="events-header">
        <h1 className="text-[28px] font-semibold leading-[1.15] tracking-[-0.015em]">Hendelser</h1>
        {isLoading || !counts ? (
          <Skeleton className="mt-2 h-5 w-96" />
        ) : (
          <p className="mt-1 text-[15px] text-muted-foreground" data-testid="events-meta">
            <span className={cn("metric", counts.open ? "font-medium text-foreground" : "")} data-testid="events-kpi-open">{counts.open}</span> {counts.open === 1 ? "åpen" : "åpne"} ikke kvittert
            {" · "}
            <span className={cn("metric", counts.critical_open ? "font-medium text-rose-700 dark:text-rose-300" : "")} data-testid="events-kpi-critical">{counts.critical_open}</span> {counts.critical_open === 1 ? "kritisk aktiv" : "kritiske aktive"}
            {" · "}
            <span className="metric" data-testid="events-kpi-30d">{counts.last_30d}</span> hendelser siste 30 dager, hvorav <span className="metric" data-testid="events-kpi-stops">{counts.stops_30d}</span> med produksjonsstans
          </p>
        )}
      </header>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-2 border-b border-border pb-4" data-testid="events-filters">
        <div className="relative w-full sm:w-64">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Søk i hendelser" className="h-9 pl-8 text-[14px]" data-testid="hendelser-sok-input" />
        </div>
        <ToggleGroup type="multiple" value={severity} onValueChange={setSeverity} className="gap-0.5" data-testid="hendelser-filter-alvorlighet">
          {Object.entries(SEVERITY).map(([k, s]) => (
            <ToggleGroupItem key={k} value={k} size="sm" className="chip" data-testid={`hendelser-filter-${k}`}>
              <span className={cn("status-dot", s.dotClass)} aria-hidden="true" /> {s.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
        <div className="flex flex-wrap items-center gap-0.5 sm:ml-auto">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="select-quiet" data-testid="hendelser-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end">
              <SelectItem value="active">Aktive (åpne + kvitterte)</SelectItem>
              <SelectItem value="open">Kun åpne</SelectItem>
              <SelectItem value="acknowledged">Kun kvitterte</SelectItem>
              <SelectItem value="resolved">Lukkede</SelectItem>
              <SelectItem value="all">Alle statuser</SelectItem>
            </SelectContent>
          </Select>
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger className="select-quiet" data-testid="hendelser-filter-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end">
              <SelectItem value="all">Alle typer</SelectItem>
              <SelectItem value="trip">Utfall</SelectItem>
              <SelectItem value="grid">Nettutfall</SelectItem>
              <SelectItem value="derate">Redusert drift</SelectItem>
              <SelectItem value="maintenance">Vedlikehold</SelectItem>
              <SelectItem value="alarm">Alarmer</SelectItem>
            </SelectContent>
          </Select>
          <Select value={plantId} onValueChange={setPlantId}>
            <SelectTrigger className="select-quiet max-w-[220px]" data-testid="hendelser-filter-kraftverk">
              <SelectValue placeholder="Kraftverk" />
            </SelectTrigger>
            <SelectContent align="end">
              <SelectItem value="all">Alle kraftverk</SelectItem>
              {(plants.data?.plants || []).map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="select-quiet" data-testid="hendelser-filter-periode">
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end">
              <SelectItem value="7">Siste 7 dager</SelectItem>
              <SelectItem value="30">Siste 30 dager</SelectItem>
              <SelectItem value="90">Siste 90 dager</SelectItem>
              <SelectItem value="365">Siste år</SelectItem>
            </SelectContent>
          </Select>
          {filtered ? (
            <button type="button" onClick={reset} className="ml-1 text-[13px] font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline" data-testid="hendelser-filter-reset">
              Nullstill
            </button>
          ) : null}
        </div>
      </div>

      <div className={cn("transition-opacity duration-200", isFetching && "opacity-70")}>
        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : data?.events?.length ? (
          <>
            <EventsTable events={data.events} onSelect={setSelected} />
            <div className="metric mt-3 text-[13px] text-muted-foreground" data-testid="events-result-count">
              {data.events.length} {data.events.length === 1 ? "hendelse" : "hendelser"}
            </div>
          </>
        ) : (
          <EmptyState title="Ingen hendelser matcher filteret" description="Prøv en lengre periode eller andre filtre." action="Vis alle" onAction={reset} testId="events-empty" />
        )}
      </div>

      <EventDetailSheet event={selected} open={!!selected} onOpenChange={(o) => !o && setSelected(null)} onUpdated={(e) => { setSelected(e); refetch(); }} />
    </div>
  );
}
